import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Users, UserCheck, UserX, DollarSign, TrendingUp, Activity } from 'lucide-react';
import { getAllUsers } from '../../services/admin/userService';
import { getFinancialDashboard } from '../../services/admin/financialService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    pendingVerification: 0,
    suspendedUsers: 0,
    totalRiders: 0,
    totalDrivers: 0
  });
  const [financial, setFinancial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadDashboardData = async () => {
    setLoading(true);
    
    // Get users
    const usersResult = await getAllUsers({ limit: 1000 });
    if (usersResult.success) {
      const users = usersResult.data.users;
      setStats({
        totalUsers: users.length,
        verifiedUsers: users.filter(u => u.verification_status === 'verified').length,
        pendingVerification: users.filter(u => u.verification_status === 'pending').length,
        suspendedUsers: users.filter(u => u.is_suspended).length,
        totalRiders: users.filter(u => u.role === 'rider').length,
        totalDrivers: users.filter(u => u.role === 'driver').length
      });
    }

    // Get financial data
    const financialResult = await getFinancialDashboard();
    if (financialResult.success) {
      setFinancial(financialResult.data);
    }

    setLastRefresh(new Date());
    setLoading(false);
  };

  const statCards = [
    { icon: Users, label: 'Total Users', value: stats.totalUsers, color: 'bg-blue-500' },
    { icon: UserCheck, label: 'Verified', value: stats.verifiedUsers, color: 'bg-green-500' },
    { icon: UserX, label: 'Pending', value: stats.pendingVerification, color: 'bg-yellow-500' },
    { icon: Activity, label: 'Suspended', value: stats.suspendedUsers, color: 'bg-red-500' },
  ];

  const vehicleData = [
    { name: 'Economy', value: financial?.by_vehicle_type?.economy?.count || 0, color: '#10b981' },
    { name: 'Premium', value: financial?.by_vehicle_type?.premium?.count || 0, color: '#3b82f6' },
    { name: 'Bike', value: financial?.by_vehicle_type?.bike?.count || 0, color: '#f59e0b' },
  ];

  const revenueData = [
    {
      name: 'Economy',
      revenue: financial?.by_vehicle_type?.economy?.revenue || 0,
      charges: financial?.by_vehicle_type?.economy?.service_charges || 0,
    },
    {
      name: 'Premium',
      revenue: financial?.by_vehicle_type?.premium?.revenue || 0,
      charges: financial?.by_vehicle_type?.premium?.service_charges || 0,
    },
    {
      name: 'Bike',
      revenue: financial?.by_vehicle_type?.bike?.revenue || 0,
      charges: financial?.by_vehicle_type?.bike?.service_charges || 0,
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
        <h1 className="text-3xl font-bold mb-2">Welcome to Ride Yachangu Admin</h1>
        <p className="text-green-100">Monitor and manage your ride-hailing platform</p>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* User Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">User Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Riders', value: stats.totalRiders, color: '#10b981' },
                    { name: 'Drivers', value: stats.totalDrivers, color: '#3b82f6' },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[{ color: '#10b981' }, { color: '#3b82f6' }].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Rides by Vehicle Type</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={vehicleData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {vehicleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Financial Overview */}
      {financial && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <p className="text-sm text-gray-600">Total Revenue</p>
              </div>
              <p className="text-2xl font-bold text-green-700">
                K{financial.overview.total_revenue.toLocaleString()}
              </p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-gray-600">Service Charges</p>
              </div>
              <p className="text-2xl font-bold text-blue-700">
                K{financial.overview.total_service_charges.toLocaleString()}
              </p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-purple-600" />
                <p className="text-sm text-gray-600">Driver Earnings</p>
              </div>
              <p className="text-2xl font-bold text-purple-700">
                K{financial.overview.total_driver_earnings.toLocaleString()}
              </p>
            </Card>
          </div>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Revenue & Service Charges by Vehicle Type</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" name="Total Revenue" />
                  <Bar dataKey="charges" fill="#3b82f6" name="Service Charges" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
